import { TextField, SelectField, YesNo, SectionIntro, US_STATES } from "./fields";
import DocUpload from "./DocUpload";

const YEARS = Array.from({ length: 30 }, (_, i) => String(new Date().getFullYear() + 1 - i));
const VEHICLE_TYPES = [["sedan", "Sedan"], ["hatchback", "Hatchback"], ["suv", "SUV"], ["crossover", "Crossover"], ["minivan", "Minivan"], ["cargo_van", "Cargo van"], ["pickup_truck", "Pickup truck"], ["other", "Other"]];

export function StepVehicleAccess({ d, set, docs, setDoc }) {
  return (
    <>
      <SectionIntro title="Vehicle Ownership & Access" text="AVP drivers use their own qualifying vehicle or an approved rental." />
      <div className="space-y-6">
        <YesNo id="has_vehicle" label="Do you currently have reliable access to a vehicle that you can use for package delivery?" value={d.has_vehicle} onChange={(v) => set("has_vehicle", v)} />
        {d.has_vehicle === "no" && (
          <div data-testid="avp-vehicle-needed-note" className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-100">
            No problem — you can finish the application now. We'll mark your file as <strong>Vehicle Needed</strong> and follow up about approved rental options.
          </div>
        )}
        {d.has_vehicle === "yes" && (
          <>
            <YesNo id="ownership" label="Is the vehicle:" value={d.ownership} onChange={(v) => set("ownership", v)} options={[["owned", "Personally owned"], ["financed", "Financed"], ["leased", "Leased"], ["rented", "Rented"], ["other_person", "Owned by another person"]]} />
            {d.ownership === "other_person" && (
              <TextField id="owner_permission" label="Identify the owner and confirm that you have permission to use the vehicle for delivery work" required value={d.owner_permission} onChange={(v) => set("owner_permission", v)} placeholder="Owner's name and relationship — e.g. 'Jane Smith (spouse), I have her permission'" />
            )}
            {d.ownership === "rented" && (
              <>
                <YesNo id="can_provide_rental_agreement" label="Can you provide a current rental agreement authorizing your possession and use of the vehicle?" value={d.can_provide_rental_agreement} onChange={(v) => set("can_provide_rental_agreement", v)} />
                {d.can_provide_rental_agreement === "yes" && setDoc && <DocUpload docKey="rental_agreement" doc={docs.rental_agreement} onChange={(v) => setDoc("rental_agreement", v)} required />}
              </>
            )}
            <YesNo id="vehicle_available_all_times" label="Is this vehicle available for delivery work during all the days and times you selected?" value={d.vehicle_available_all_times} onChange={(v) => set("vehicle_available_all_times", v)} />
          </>
        )}
      </div>
    </>
  );
}

export function StepVehicle({ d, set }) {
  return (
    <>
      <SectionIntro title="Vehicle Information" text="Tell us about the vehicle you plan to use." />
      <div className="grid sm:grid-cols-2 gap-5">
        <SelectField id="vehicle_type" label="Vehicle type" required value={d.vehicle_type} onChange={(v) => set("vehicle_type", v)} options={VEHICLE_TYPES} />
        <SelectField id="year" label="Vehicle year" required value={d.year} onChange={(v) => set("year", v)} options={YEARS} placeholder="Year" />
        <TextField id="make" label="Vehicle make" required value={d.make} onChange={(v) => set("make", v)} placeholder="e.g. Toyota" />
        <TextField id="model" label="Vehicle model" required value={d.model} onChange={(v) => set("model", v)} placeholder="e.g. Sienna" />
        <TextField id="color" label="Vehicle color" required value={d.color} onChange={(v) => set("color", v)} />
        <TextField id="mileage" label="Current mileage" type="number" required value={d.mileage} onChange={(v) => set("mileage", v)} inputMode="numeric" min="0" />
        <TextField id="plate" label="License-plate number" required value={d.plate} onChange={(v) => set("plate", v.toUpperCase())} />
        <SelectField id="plate_state" label="License-plate state" required value={d.plate_state} onChange={(v) => set("plate_state", v)} options={US_STATES} placeholder="State" />
        <div className="sm:col-span-2"><TextField id="vin" label="VIN — may be provided now or during vehicle verification" value={d.vin} onChange={(v) => set("vin", v.toUpperCase())} maxLength={17} placeholder="17-character VIN (optional for now)" /></div>
      </div>
      <div className="mt-8 space-y-6">
        <YesNo id="cargo_space" label="Does the vehicle have enough clean, usable cargo space to transport multiple packages safely?" value={d.cargo_space} onChange={(v) => set("cargo_space", v)} />
        <YesNo id="enclosed_lockable" label="Can packages be kept inside a fully enclosed and lockable portion of the vehicle?" value={d.enclosed_lockable} onChange={(v) => set("enclosed_lockable", v)} />
        <YesNo id="safe_reliable" label="Is the vehicle currently safe, reliable, and free from major mechanical or body-condition problems?" value={d.safe_reliable} onChange={(v) => set("safe_reliable", v)} />
        <YesNo id="equipment_ok" label="Does the vehicle have working seat belts, headlights, brake lights, turn signals, mirrors, tires, brakes, and windshield wipers?" value={d.equipment_ok} onChange={(v) => set("equipment_ok", v)} />
      </div>
    </>
  );
}

export function StepPickup({ d, set, docs, setDoc }) {
  const flagged = d.bed_covered === "no" || d.lockable === "no";
  return (
    <>
      <SectionIntro title="Pickup-Truck Requirements" text="Packages must stay covered, dry, and secured. Open beds cannot be used for priority packages." />
      <div className="space-y-6">
        <YesNo id="bed_covered" label="Is the pickup bed completely covered?" value={d.bed_covered} onChange={(v) => set("bed_covered", v)} />
        <YesNo id="weather_resistant" label="Is the covered cargo area weather-resistant?" value={d.weather_resistant} onChange={(v) => set("weather_resistant", v)} />
        <YesNo id="lockable" label="Can the pickup bed or cargo cover be securely locked?" value={d.lockable} onChange={(v) => set("lockable", v)} />
        <YesNo id="cover_type" label="What type of cover does the vehicle have?" required={false} value={d.cover_type} onChange={(v) => set("cover_type", v)} options={[["hard_tonneau", "Hard tonneau cover"], ["camper_shell", "Lockable camper shell"], ["bed_cap", "Lockable bed cap"], ["other", "Other"]]} />
        {flagged && (
          <div data-testid="avp-pickup-review-note" className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-100">
            Your vehicle will be marked <strong>Vehicle Review Required</strong>. You can still complete the application — our team will discuss cover and lock options with you.
          </div>
        )}
        {setDoc && <div className="grid sm:grid-cols-2 gap-4">
          <DocUpload docKey="pickup_bed_photo" doc={docs.pickup_bed_photo} onChange={(v) => setDoc("pickup_bed_photo", v)} imageOnly required />
          <DocUpload docKey="pickup_lock_photo" doc={docs.pickup_lock_photo} onChange={(v) => setDoc("pickup_lock_photo", v)} imageOnly required />
        </div>}
      </div>
    </>
  );
}

export function StepDocuments({ docs, setDoc, isRented }) {
  return (
    <>
      <SectionIntro title="Vehicle Documents" text="You don't need to upload everything today to submit. However, every required document must be submitted and approved before your vehicle can be activated." />
      <div className="grid sm:grid-cols-2 gap-4">
        <DocUpload docKey="registration" doc={docs.registration} onChange={(v) => setDoc("registration", v)} />
        <DocUpload docKey="insurance" doc={docs.insurance} onChange={(v) => setDoc("insurance", v)} />
        <DocUpload docKey="inspection" doc={docs.inspection} onChange={(v) => setDoc("inspection", v)} allowNA />
        <DocUpload docKey="vin_photo" doc={docs.vin_photo} onChange={(v) => setDoc("vin_photo", v)} imageOnly />
        {isRented && <div className="sm:col-span-2"><DocUpload docKey="rental_agreement" doc={docs.rental_agreement} onChange={(v) => setDoc("rental_agreement", v)} required /></div>}
      </div>
      <p className="mt-4 text-xs text-slate-500">Accepted: PDF, JPG, JPEG, HEIC, PNG · up to 10MB each. Documents are stored securely and visible only to authorized Integrity Delivered administrators.</p>
    </>
  );
}

export function StepPhotos({ docs, setDoc, isPickup }) {
  return (
    <>
      <SectionIntro title="Vehicle Photographs" text="Four current, full-color photographs showing the complete vehicle, plus the empty cargo area. Photos must be clear, current, in color, and show the complete vehicle." />
      <div className="grid sm:grid-cols-2 gap-4">
        {["photo_front", "photo_rear", "photo_driver", "photo_passenger", "photo_cargo"].map((k) => (
          <DocUpload key={k} docKey={k} doc={docs[k]} onChange={(v) => setDoc(k, v)} imageOnly />
        ))}
        {isPickup && (
          <>
            <DocUpload docKey="pickup_bed_photo" doc={docs.pickup_bed_photo} onChange={(v) => setDoc("pickup_bed_photo", v)} imageOnly />
            <DocUpload docKey="pickup_lock_photo" doc={docs.pickup_lock_photo} onChange={(v) => setDoc("pickup_lock_photo", v)} imageOnly />
          </>
        )}
      </div>
      <p className="mt-4 text-xs text-slate-500">Not ready? Check "I will provide this during onboarding" on any photo — you'll still be able to submit today.</p>
    </>
  );
}
